import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, CheckCircle2, Factory, Home, LandPlot, MapPin, Pencil, Plus, Star, Trash2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  MAP_PROVIDER_GOOGLE_CODE,
  MAP_PROVIDER_OPENSTREETMAP_CODE,
} from '@/constants/map-providers'
import { getErrorMessage } from '@/lib/api'
import { getMapSearchAddress, getMapSearchCity } from '@/lib/map-location'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import {
  USER_ADDRESS_TYPE,
  buildAddressAccessSummary,
  getAddressTypeLabel,
  getAddressTypeNameForPayload,
  isBusinessHoursAddressType,
  normalizeAddressTypeForForm,
  toTimeInputValue,
} from '@/lib/user-address'
import { isOrganizationServiceVariant } from '@/lib/service-variant-types'
import { orgTreeApi } from '@/services/org-tree'
import { orgUsersApi, type OrgUserAddress } from '@/services/org-users'

type LocationSearchOption = {
  id: string
  label: string
  lat: string
  lon: string
  doorNo?: string
  street?: string
  city?: string
  zipcode?: string
}

type OrgUserAddressManagerProps = {
  userId: string
  entityDisplayName?: string
  email?: string | null
  phone?: string | null
  entityLabel: string
  detailQueryKey: readonly unknown[]
  initialAddresses?: OrgUserAddress[] | null
  defaultAddressType?: number
  showBusinessHoursFields?: boolean
  canCreateAddresses: boolean
  canUpdateAddresses: boolean
  canDeleteAddresses: boolean
  canSetDefaultAddresses: boolean
}

const getAddressLines = (address?: OrgUserAddress | null) => {
  if (!address) {
    return []
  }

  const line1 = [address.door_no, address.building_name].filter(Boolean).join(', ')
  const line2 = [address.floor_line, address.street_line].filter(Boolean).join(', ')
  const line3 = [address.city_name, address.state_name, address.zipcode].filter(Boolean).join(', ')
  const line4 = address.country_name || ''

  return [line1, line2, line3, line4].filter(Boolean)
}

const normalizeLabel = (value?: string | null) => value?.trim().toLowerCase() ?? ''

const USER_ADDRESS_STATUS_ACTIVE = 1
const USER_ADDRESS_STATUS_INACTIVE = 2

const getAddressTypeIcon = (value?: number | null) => {
  if (value === USER_ADDRESS_TYPE.REMOTE_SITE) {
    return LandPlot
  }
  if (value === USER_ADDRESS_TYPE.INDUSTRIAL) {
    return Factory
  }
  if (value === USER_ADDRESS_TYPE.BUSINESS) {
    return Building2
  }
  if (value === USER_ADDRESS_TYPE.OTHER) {
    return MapPin
  }
  return Home
}

const PENDING_APPROVAL_ADDRESS_WRITE_MESSAGE = 'Address changes are unavailable until the branch or organisation is approved.'

const getAddressWriteErrorMessage = (error: unknown) => {
  const message = getErrorMessage(error)
  if (message.toLowerCase().includes('branch/organisation is pending. write operations are forbidden.')) {
    return PENDING_APPROVAL_ADDRESS_WRITE_MESSAGE
  }

  return message
}

const toInputValue = (value?: string | number | null) => (value == null ? '' : String(value))

export default function OrgUserAddressManager({
  userId,
  entityDisplayName,
  email,
  phone,
  entityLabel,
  detailQueryKey,
  initialAddresses,
  defaultAddressType = USER_ADDRESS_TYPE.RESIDENTIAL,
  showBusinessHoursFields = false,
  canCreateAddresses,
  canUpdateAddresses,
  canDeleteAddresses,
  canSetDefaultAddresses,
}: OrgUserAddressManagerProps) {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const queryClient = useQueryClient()
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false)
  const [editingAddressId, setEditingAddressId] = useState<string | number | null>(null)
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedAddressType, setSelectedAddressType] = useState<number>(USER_ADDRESS_TYPE.RESIDENTIAL)
  const [latitudeDraft, setLatitudeDraft] = useState('')
  const [longitudeDraft, setLongitudeDraft] = useState('')
  const [doorNoDraft, setDoorNoDraft] = useState('')
  const [streetLineDraft, setStreetLineDraft] = useState('')
  const [cityNameDraft, setCityNameDraft] = useState('')
  const [zipcodeDraft, setZipcodeDraft] = useState('')
  const [accessInstructionsDraft, setAccessInstructionsDraft] = useState('')
  const [locationPinDraft, setLocationPinDraft] = useState('')
  const [entryCodeDraft, setEntryCodeDraft] = useState('')
  const [openingTimeDraft, setOpeningTimeDraft] = useState('')
  const [closingTimeDraft, setClosingTimeDraft] = useState('')
  const [saturdayClosedDraft, setSaturdayClosedDraft] = useState(false)
  const [sundayClosedDraft, setSundayClosedDraft] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')
  const [locationSearchOptions, setLocationSearchOptions] = useState<LocationSearchOption[]>([])
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const [searchFallbackUsed, setSearchFallbackUsed] = useState(false)
  const [lastResolvedProvider, setLastResolvedProvider] = useState<'googlemaps' | 'openstreetmap'>('openstreetmap')
  const [lastProviderSource, setLastProviderSource] = useState<'org-instance' | 'service-instance'>('service-instance')
  const addressQueryKey = [...detailQueryKey, 'addresses', userId]

  const { data: addresses = [] } = useQuery({
    queryKey: addressQueryKey,
    queryFn: () => orgUsersApi.listAddresses(userId, { includeInactive: true }),
    enabled: Boolean(userId),
    placeholderData: initialAddresses ?? [],
    refetchOnMount: 'always',
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  const statesQuery = useQuery({
    queryKey: ['org-tree-states', selectedCountryId],
    queryFn: () => orgTreeApi.listStates(selectedCountryId),
    enabled: Boolean(selectedCountryId),
    staleTime: 60_000,
  })

  const serviceInstanceQuery = useQuery({
    queryKey: ['org-tree-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(serviceInstanceId as string),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const editingAddress = editingAddressId == null
    ? null
    : addresses.find((address) => String(address.id) === String(editingAddressId)) ?? null
  const normalizedEntityDisplayName = entityDisplayName?.trim() || null
  const isOrganizationInstanceContext = isOrganizationServiceVariant(
    serviceInstanceQuery.data?.service_data?.variant_unique ?? serviceInstanceQuery.data?.service_variant,
    serviceInstanceQuery.data?.service_variant_name,
  )
  const providerSourceLabel =
    lastProviderSource === 'org-instance' || isOrganizationInstanceContext ? 'Organization' : 'Service Branch'

  useEffect(() => {
    if (!isAddressDialogOpen || selectedCountryId) {
      return
    }

    if (!editingAddress) {
      return
    }

    const matchedCountryId = editingAddress.country != null
      ? String(editingAddress.country)
      : String(
          countriesQuery.data?.find(
            (country) => normalizeLabel(country.country_name) === normalizeLabel(editingAddress.country_name),
          )?.id ?? '',
        )

    if (matchedCountryId) {
      setSelectedCountryId(matchedCountryId)
    }
  }, [countriesQuery.data, editingAddress, isAddressDialogOpen, selectedCountryId])

  useEffect(() => {
    if (!isAddressDialogOpen || selectedStateId || !editingAddress || !selectedCountryId) {
      return
    }

    const matchedStateId = editingAddress.state != null
      ? String(editingAddress.state)
      : String(
          statesQuery.data?.find(
            (state) => normalizeLabel(state.name) === normalizeLabel(editingAddress.state_name),
          )?.id ?? '',
        )

    if (matchedStateId) {
      setSelectedStateId(matchedStateId)
    }
  }, [editingAddress, isAddressDialogOpen, selectedCountryId, selectedStateId, statesQuery.data])

  useEffect(() => {
    if (!isAddressDialogOpen) {
      return
    }

    setSelectedAddressType(normalizeAddressTypeForForm(editingAddress?.type))
    setDoorNoDraft(toInputValue(editingAddress?.door_no))
    setStreetLineDraft(toInputValue(editingAddress?.street_line))
    setCityNameDraft(toInputValue(editingAddress?.city_name))
    setZipcodeDraft(toInputValue(editingAddress?.zipcode))
    setLatitudeDraft(toInputValue(editingAddress?.latitude))
    setLongitudeDraft(toInputValue(editingAddress?.longitude))
    setAccessInstructionsDraft(toInputValue(editingAddress?.access_instructions))
    setLocationPinDraft(toInputValue(editingAddress?.location_pin))
    setEntryCodeDraft(toInputValue(editingAddress?.entry_code))
    setOpeningTimeDraft(toTimeInputValue(editingAddress?.opening_time))
    setClosingTimeDraft(toTimeInputValue(editingAddress?.closing_time))
    setSaturdayClosedDraft(Boolean(editingAddress?.saturday_closed))
    setSundayClosedDraft(Boolean(editingAddress?.sunday_closed))
    setLocationSearch('')
    setLocationSearchOptions([])
    setSearchFallbackUsed(false)
    setLastResolvedProvider('openstreetmap')
    setLastProviderSource('service-instance')
  }, [editingAddress, isAddressDialogOpen])

  useEffect(() => {
    if (!isAddressDialogOpen) {
      return
    }

    const query = locationSearch.trim()
    if (query.length < 3) {
      setLocationSearchOptions([])
      setIsSearchingLocation(false)
      return
    }

    const controller = new AbortController()
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearchingLocation(true)
        if (!serviceInstanceId) {
          setLocationSearchOptions([])
          return
        }

        const payload = await orgTreeApi.searchServiceInstanceLocation(serviceInstanceId, query, 5)
        setSearchFallbackUsed(Boolean(payload.fallback_used))
        setLastResolvedProvider(payload.provider === 'googlemaps' ? 'googlemaps' : 'openstreetmap')
        setLastProviderSource(payload.provider_source === 'org-instance' ? 'org-instance' : 'service-instance')
        setLocationSearchOptions(
          payload.results
            .map((entry) => ({
              id: String(entry.id ?? '').trim(),
              label: String(entry.label ?? '').trim(),
              lat: String(entry.lat ?? '').trim(),
              lon: String(entry.lon ?? '').trim(),
              doorNo: String(entry.door_no ?? '').trim(),
              street: String(entry.street ?? '').trim(),
              city: getMapSearchCity(entry),
              zipcode: String(entry.zipcode ?? '').trim(),
            }))
            .filter((entry) => entry.id && entry.label && entry.lat && entry.lon),
        )
      } catch (error) {
        if ((error as { name?: string })?.name !== 'AbortError') {
          setLocationSearchOptions([])
          setSearchFallbackUsed(false)
          setLastResolvedProvider('openstreetmap')
          setLastProviderSource('service-instance')
        }
      } finally {
        setIsSearchingLocation(false)
      }
    }, 350)

    return () => {
      controller.abort()
      window.clearTimeout(timeoutId)
    }
  }, [isAddressDialogOpen, locationSearch, serviceInstanceId])

  const invalidateAddressState = async () => {
    await queryClient.invalidateQueries({ queryKey: detailQueryKey })
    await queryClient.invalidateQueries({ queryKey: addressQueryKey })
  }

  const saveAddressMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (editingAddressId != null) {
        return orgUsersApi.updateAddress(editingAddressId, payload)
      }

      return orgUsersApi.createAddress(userId, payload)
    },
    onSuccess: async () => {
      await invalidateAddressState()
      setIsAddressDialogOpen(false)
      setEditingAddressId(null)
      toast.success(editingAddressId != null ? 'Address updated successfully' : 'Address added successfully')
    },
    onError: (error) => {
      toast.error(getAddressWriteErrorMessage(error))
    },
  })

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string | number) => orgUsersApi.deleteAddress(addressId),
    onSuccess: async () => {
      await invalidateAddressState()
      toast.success('Address deleted successfully')
    },
    onError: (error) => {
      toast.error(getAddressWriteErrorMessage(error))
    },
  })

  const setDefaultAddressMutation = useMutation({
    mutationFn: (addressId: string | number) => orgUsersApi.setDefaultAddress(userId, addressId),
    onSuccess: async () => {
      await invalidateAddressState()
      toast.success('Default address updated successfully')
    },
    onError: (error) => {
      toast.error(getAddressWriteErrorMessage(error))
    },
  })

  const toggleAddressStatusMutation = useMutation({
    mutationFn: ({ addressId, nextStatus }: { addressId: string | number; nextStatus: number }) =>
      orgUsersApi.updateAddress(addressId, { status: nextStatus }),
    onSuccess: async (_data, variables) => {
      await invalidateAddressState()
      toast.success(variables.nextStatus === USER_ADDRESS_STATUS_ACTIVE ? 'Address activated successfully' : 'Address deactivated successfully')
    },
    onError: (error) => {
      toast.error(getAddressWriteErrorMessage(error))
    },
  })

  const normalizedDefaultAddressType = normalizeAddressTypeForForm(defaultAddressType)
  const shouldShowHoursFields = showBusinessHoursFields || isBusinessHoursAddressType(selectedAddressType)

  const openCreateAddressDialog = () => {
    setEditingAddressId(null)
    setSelectedAddressType(normalizedDefaultAddressType)
    setSelectedCountryId('')
    setSelectedStateId('')
    setIsAddressDialogOpen(true)
  }

  const openEditAddressDialog = (addressId: string | number) => {
    setEditingAddressId(addressId)
    const address = addresses.find((item) => String(item.id) === String(addressId))
    setSelectedAddressType(normalizeAddressTypeForForm(address?.type))
    setSelectedCountryId('')
    setSelectedStateId('')
    setIsAddressDialogOpen(true)
  }

  const handleAddressSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formData = new FormData(event.currentTarget)
    const normalize = (key: string) => {
      const value = formData.get(key)
      if (typeof value !== 'string') {
        return undefined
      }

      const trimmed = value.trim()
      return trimmed.length > 0 ? trimmed : undefined
    }

    const addressType = normalizeAddressTypeForForm(selectedAddressType)
    const otherAddressFor = normalize('address_for')

    if (addressType === USER_ADDRESS_TYPE.OTHER && !otherAddressFor) {
      toast.error('Address For is required when Address Type is Other')
      return
    }

    saveAddressMutation.mutate({
      name: getAddressTypeNameForPayload(addressType, otherAddressFor),
      email: email || undefined,
      mobile_number: phone || undefined,
      optional_mobile_number: normalize('optional_mobile_number'),
      type: addressType,
      door_no: normalize('door_no'),
      building_name: normalize('building_name'),
      floor_line: normalize('floor_line'),
      street_line: normalize('street_line'),
      zipcode: normalize('zipcode'),
      city_name: normalize('city_name'),
      state: selectedStateId || undefined,
      country: selectedCountryId || undefined,
      latitude: latitudeDraft.trim() || undefined,
      longitude: longitudeDraft.trim() || undefined,
      access_instructions: accessInstructionsDraft.trim() || undefined,
      location_pin: locationPinDraft.trim() || undefined,
      entry_code: entryCodeDraft.trim() || undefined,
      opening_time: openingTimeDraft.trim() || undefined,
      closing_time: closingTimeDraft.trim() || undefined,
      saturday_closed: saturdayClosedDraft,
      sunday_closed: sundayClosedDraft,
    })
  }

  return (
    <>
      <div className="space-y-4">
        {addresses.length ? (
          addresses.map((address) => {
            const isActive = (address.status ?? USER_ADDRESS_STATUS_ACTIVE) === USER_ADDRESS_STATUS_ACTIVE
            const isDefault = Boolean(address.default)
            const typeLabel = getAddressTypeLabel(address.type)
            const AddressTypeIcon = getAddressTypeIcon(address.type)
            const addressLabel = isDefault ? 'Primary address' : `${typeLabel} address`

            return (
              <div
                key={address.id}
                className={`relative overflow-hidden rounded-3xl border bg-gradient-to-br p-5 shadow-sm transition-opacity ${isActive ? 'border-border/70 from-background to-muted/20 opacity-100' : 'border-border/40 from-muted/30 to-muted/10 opacity-70'}`}
              >
                {!isActive ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-3xl bg-background/50 backdrop-blur-[1px]">
                    <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/90 px-4 py-2 shadow-sm">
                      <XCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Address inactive</span>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <AddressTypeIcon className="h-4 w-4" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-foreground">{addressLabel}</p>
                        <Badge variant="outline">{typeLabel}</Badge>
                        {isDefault ? <Badge variant="secondary">Default</Badge> : null}
                        {isActive
                          ? <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />Active</Badge>
                          : <Badge variant="outline" className="gap-1 text-muted-foreground"><XCircle className="h-3 w-3" />Inactive</Badge>}
                      </div>
                      <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Address Type: {typeLabel}</p>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {getAddressLines(address).map((line) => (
                          <p key={`${address.id}-${line}`}>{line}</p>
                        ))}
                        {buildAddressAccessSummary(address).map((line) => (
                          <p key={`${address.id}-access-${line}`}>{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!isDefault && isActive && canSetDefaultAddresses ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDefaultAddressMutation.mutate(address.id)}
                        disabled={setDefaultAddressMutation.isPending}
                      >
                        <Star className="mr-2 h-4 w-4" />
                        Set default
                      </Button>
                    ) : null}
                    {canUpdateAddresses ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={isActive ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-700 border-amber-200' : 'text-green-600 hover:bg-green-50 hover:text-green-700 border-green-200'}
                        onClick={() => toggleAddressStatusMutation.mutate({
                          addressId: address.id,
                          nextStatus: isActive ? USER_ADDRESS_STATUS_INACTIVE : USER_ADDRESS_STATUS_ACTIVE,
                        })}
                        disabled={toggleAddressStatusMutation.isPending}
                      >
                        {isActive ? <XCircle className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                        {toggleAddressStatusMutation.isPending ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    ) : null}
                    {canUpdateAddresses ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${entityLabel} ${addressLabel.toLowerCase()}`}
                        onClick={() => openEditAddressDialog(address.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    ) : null}
                    {canDeleteAddresses ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        aria-label={`Delete ${entityLabel} ${addressLabel.toLowerCase()}`}
                        onClick={() => deleteAddressMutation.mutate(address.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="flex min-h-[220px] items-center justify-center rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-primary/5 via-background to-muted/20 text-center">
            <div>
              <MapPin className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="font-medium text-foreground">No address available</p>
              <p className="text-sm text-muted-foreground">Add residential, business, industrial, remote site, or other addresses for this {entityLabel} profile.</p>
            </div>
          </div>
        )}
        {canCreateAddresses ? (
          <Button type="button" variant="outline" className="w-full" onClick={openCreateAddressDialog}>
            <Plus className="mr-2 h-4 w-4" />
            {addresses.length ? 'Add Another Address' : 'Add New Address'}
          </Button>
        ) : null}
      </div>

      <Dialog open={isAddressDialogOpen} onOpenChange={(open) => {
        setIsAddressDialogOpen(open)
        if (!open) {
          setEditingAddressId(null)
          setSelectedAddressType(normalizedDefaultAddressType)
          setSelectedCountryId('')
          setSelectedStateId('')
          setDoorNoDraft('')
          setStreetLineDraft('')
          setCityNameDraft('')
          setZipcodeDraft('')
          setLatitudeDraft('')
          setLongitudeDraft('')
          setAccessInstructionsDraft('')
          setLocationPinDraft('')
          setEntryCodeDraft('')
          setOpeningTimeDraft('')
          setClosingTimeDraft('')
          setSaturdayClosedDraft(false)
          setSundayClosedDraft(false)
          setLocationSearch('')
          setLocationSearchOptions([])
          setSearchFallbackUsed(false)
          setLastResolvedProvider('openstreetmap')
          setLastProviderSource('service-instance')
        }
      }}>
        <DialogContent className="flex max-h-[76vh] flex-col overflow-hidden p-0 sm:max-w-lg">
          <form onSubmit={handleAddressSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogHeader className="border-b px-5 py-2.5">
              <DialogTitle>
                {editingAddress
                  ? normalizedEntityDisplayName
                    ? `Edit address for ${normalizedEntityDisplayName}`
                    : `Edit ${entityLabel} address`
                  : normalizedEntityDisplayName
                    ? `Add address for ${normalizedEntityDisplayName}`
                    : `Add ${entityLabel} address`}
              </DialogTitle>
              <DialogDescription>
                Save the address details used for this {entityLabel} profile{normalizedEntityDisplayName ? ` (${normalizedEntityDisplayName})` : ''}.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-5 py-2.5">
              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Search Location</p>
                <div className="mt-2 grid gap-2">
                  <Label htmlFor="location_search">Search place</Label>
                  <Input
                    id="location_search"
                    value={locationSearch}
                    onChange={(event) => setLocationSearch(event.target.value)}
                    placeholder="Search area or landmark to fill coordinates"
                  />
                  {isSearchingLocation ? (
                    <p className="text-xs text-muted-foreground">Searching locations...</p>
                  ) : null}
                  {!serviceInstanceId ? (
                    <p className="text-xs text-amber-600">Organization context is missing; location search is unavailable.</p>
                  ) : null}
                  {searchFallbackUsed ? (
                    <p className="text-xs text-amber-600">Google search fallback triggered. Showing OpenStreetMap results from {providerSourceLabel} settings.</p>
                  ) : null}
                  {!isSearchingLocation && locationSearch.trim().length >= 3 && locationSearchOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No matching locations found.</p>
                  ) : null}
                  {locationSearchOptions.length > 0 ? (
                    <div className="max-h-36 overflow-y-auto rounded-xl border border-border/60 bg-background">
                      {locationSearchOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="w-full border-b border-border/50 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/40"
                          onClick={() => {
                            setDoorNoDraft(option.doorNo ?? '')
                            setStreetLineDraft(option.street ?? '')
                            setCityNameDraft(option.city ?? '')
                            setZipcodeDraft(option.zipcode ?? '')
                            setLatitudeDraft(option.lat)
                            setLongitudeDraft(option.lon)
                            setLocationSearch(getMapSearchAddress({
                              label: option.label,
                              doorNo: option.doorNo,
                              street: option.street,
                              city: option.city,
                              zipcode: option.zipcode,
                            }) || option.label)
                            setLocationSearchOptions([])
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Identity</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="address_type">Address Type</Label>
                    <Select
                      name="address_type"
                      value={String(selectedAddressType)}
                      onValueChange={(value) => setSelectedAddressType(normalizeAddressTypeForForm(Number(value)))}
                    >
                      <SelectTrigger id="address_type" className="w-full" aria-label="Address Type">
                        <SelectValue placeholder="Select address type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(USER_ADDRESS_TYPE.RESIDENTIAL)}>Residential</SelectItem>
                        <SelectItem value={String(USER_ADDRESS_TYPE.BUSINESS)}>Business</SelectItem>
                        <SelectItem value={String(USER_ADDRESS_TYPE.INDUSTRIAL)}>Industrial</SelectItem>
                        <SelectItem value={String(USER_ADDRESS_TYPE.REMOTE_SITE)}>Remote Site</SelectItem>
                        <SelectItem value={String(USER_ADDRESS_TYPE.OTHER)}>Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedAddressType === USER_ADDRESS_TYPE.OTHER ? (
                    <div className="grid gap-2 sm:col-span-2">
                      <Label htmlFor="address_for">Address For *</Label>
                      <Input
                        id="address_for"
                        name="address_for"
                        defaultValue={editingAddress?.type === USER_ADDRESS_TYPE.OTHER ? toInputValue(editingAddress?.name) : ''}
                        placeholder="e.g. Billing, Warehouse, Vacation Home"
                        required
                      />
                    </div>
                  ) : null}
                  <div className="grid gap-2">
                    <Label htmlFor="door_no">Door No</Label>
                    <Input id="door_no" name="door_no" value={doorNoDraft} onChange={(event) => setDoorNoDraft(event.target.value)} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="optional_mobile_number">Alternate Phone</Label>
                    <Input id="optional_mobile_number" name="optional_mobile_number" type="tel" defaultValue={editingAddress?.optional_mobile_number ?? ''} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Access</p>
                <div className="mt-2 grid gap-2">
                  <div className="grid gap-2">
                    <Label htmlFor="access_instructions">Access instructions</Label>
                    <Textarea
                      id="access_instructions"
                      value={accessInstructionsDraft}
                      onChange={(event) => setAccessInstructionsDraft(event.target.value)}
                      placeholder="Gate location, parking, buzzer name, etc."
                      rows={3}
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="location_pin">Location pin</Label>
                      <Input
                        id="location_pin"
                        value={locationPinDraft}
                        onChange={(event) => setLocationPinDraft(event.target.value)}
                        placeholder="Map pin or site reference"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="entry_code">Entry code</Label>
                      <Input
                        id="entry_code"
                        value={entryCodeDraft}
                        onChange={(event) => setEntryCodeDraft(event.target.value)}
                        placeholder="Door or gate code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {shouldShowHoursFields ? (
              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Hours</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="opening_time">Opening time</Label>
                    <Input
                      id="opening_time"
                      type="time"
                      value={openingTimeDraft}
                      onChange={(event) => setOpeningTimeDraft(event.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="closing_time">Closing time</Label>
                    <Input
                      id="closing_time"
                      type="time"
                      value={closingTimeDraft}
                      onChange={(event) => setClosingTimeDraft(event.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <Checkbox
                      id="saturday_closed"
                      checked={saturdayClosedDraft}
                      onCheckedChange={(checked) => setSaturdayClosedDraft(checked === true)}
                    />
                    <Label htmlFor="saturday_closed" className="font-normal">Closed on Saturday</Label>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <Checkbox
                      id="sunday_closed"
                      checked={sundayClosedDraft}
                      onCheckedChange={(checked) => setSundayClosedDraft(checked === true)}
                    />
                    <Label htmlFor="sunday_closed" className="font-normal">Closed on Sunday</Label>
                  </div>
                </div>
              </div>
              ) : null}

              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Location</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="building_name">Building Name</Label>
                    <Input id="building_name" name="building_name" defaultValue={editingAddress?.building_name ?? ''} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="floor_line">Floor / Unit</Label>
                    <Input id="floor_line" name="floor_line" defaultValue={editingAddress?.floor_line ?? ''} />
                  </div>
                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="street_line">Street</Label>
                    <Input id="street_line" name="street_line" value={streetLineDraft} onChange={(event) => setStreetLineDraft(event.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="city_name">City</Label>
                    <Input id="city_name" name="city_name" value={cityNameDraft} onChange={(event) => setCityNameDraft(event.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="zipcode">Postcode</Label>
                    <Input id="zipcode" name="zipcode" value={zipcodeDraft} onChange={(event) => setZipcodeDraft(event.target.value)} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={selectedCountryId} onValueChange={(value) => {
                      setSelectedCountryId(value)
                      setSelectedStateId('')
                    }}>
                      <SelectTrigger id="country" className="w-full" aria-label="Country">
                        <SelectValue placeholder="Choose country" />
                      </SelectTrigger>
                      <SelectContent>
                        {(countriesQuery.data ?? []).map((country) => (
                          <SelectItem key={String(country.id)} value={String(country.id)}>
                            {country.country_name || `Country ${country.id}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="state">State / Province</Label>
                    <Select value={selectedStateId} onValueChange={setSelectedStateId} disabled={!selectedCountryId || statesQuery.isLoading}>
                      <SelectTrigger id="state" className="w-full" aria-label="State / Province">
                        <SelectValue placeholder={selectedCountryId ? 'Choose state' : 'Select a country first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(statesQuery.data ?? []).map((state) => (
                          <SelectItem key={String(state.id)} value={String(state.id)}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Coordinates</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Optional</Badge>
                    <Badge variant="secondary">
                      {lastResolvedProvider === 'googlemaps'
                        ? `Google Maps (${MAP_PROVIDER_GOOGLE_CODE}) · ${providerSourceLabel}`
                        : `OpenStreetMap (${MAP_PROVIDER_OPENSTREETMAP_CODE}) · ${providerSourceLabel}`}
                    </Badge>
                  </div>
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input id="latitude" name="latitude" value={latitudeDraft} onChange={(event) => setLatitudeDraft(event.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input id="longitude" name="longitude" value={longitudeDraft} onChange={(event) => setLongitudeDraft(event.target.value)} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="border-t px-5 py-2.5">
              <Button type="button" variant="outline" onClick={() => setIsAddressDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saveAddressMutation.isPending}>
                {saveAddressMutation.isPending ? 'Saving...' : editingAddress ? 'Update Address' : 'Save Address'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}